import { locate, locateNode, getBox } from "@dhx/ts-common/html";
import { collectionStore } from "./CollectionStore";
import { TreeCollection } from "./treecollection";
import { DropBehaviour, DragEvents, DragMode, Id, ITransferData, DropPosition, ICopyObject } from "./types";
import { isTreeCollection } from "./helpers";


function getPosition(e: MouseEvent) {
	const y = e.clientY;
	const element = locateNode(e);
	if (!element) {
		return null;
	}
	const treeLine: HTMLElement = element.childNodes[0] as HTMLElement;

	const {top, height} = treeLine.getBoundingClientRect();
	return (y - top) / height;
}

function dragEventContent(element: HTMLElement, elements: NodeList) {
	const rect = element.getBoundingClientRect();
	const ghost = document.createElement("div");
	const clone = element.cloneNode(true) as HTMLElement;
	clone.style.width = rect.width + "px";
	clone.style.height = rect.height + "px";
	clone.style.maxHeight = rect.height + "px";
	clone.style.fontSize = window.getComputedStyle(element.parentElement).fontSize;
	clone.style.opacity = "0.8";
	clone.style.fontSize = window.getComputedStyle(element.parentElement).fontSize;
	ghost.appendChild(clone);
	if (elements && elements.length) {
		elements.forEach((node, key)=> {
			const nodeClone = node.cloneNode(true) as HTMLElement;
			nodeClone.style.width = rect.width + "px";
			nodeClone.style.height = rect.height + "px";
			nodeClone.style.maxHeight = rect.height + "px";
			nodeClone.style.top = ((key + 1)*12 - rect.height) - (rect.height * key) + "px";
			nodeClone.style.left = (key + 1)*12 + "px";
			nodeClone.style.opacity = "0.6";
			nodeClone.style.zIndex = `${-key - 1}`;
			ghost.appendChild(nodeClone);
		});
	}
	ghost.className = "dhx_drag-ghost";
	return ghost;
}

class DragManager {
	private _transferData: ITransferData = {};
	private _canMove: boolean = true;

	private _lastId: string;
	private _lastCollectionId: string;
	private _selectedIds: string[] = [];
	private _itemsForGhost: NodeList;

	public setItem(id: Id, item: any) {
		collectionStore.setItem(id, item);
	}
	public onMouseDown(e: MouseEvent, selectedIds: string[], itemsForGhost: NodeList) { // onmousedown only for target objects
		if (e.which !== 1) {
			return;
		}
		e.preventDefault();
		document.addEventListener("mousemove", this._onMouseMove);
		document.addEventListener("mouseup", this._onMouseUp);

		const item = locateNode(e, "dhx_id") as HTMLElement;
		const id = item && item.getAttribute("dhx_id");
		const targetId = locate(e, "dhx_widget_id");

		if (selectedIds && selectedIds.indexOf(id) !== - 1 && selectedIds.length > 1) {
			this._selectedIds = selectedIds;
			this._itemsForGhost = itemsForGhost;
		} else {
			this._selectedIds = [];
			this._itemsForGhost = null;
		}
		if (id && targetId) {
			const {left, top} = getBox(item);
			this._transferData.initXOffset = e.pageX - left;
			this._transferData.initYOffset = e.pageY - top;
			this._transferData.x = e.pageX;
			this._transferData.y = e.pageY;
			this._transferData.targetId = targetId;
			this._transferData.id = id;
			this._transferData.item = item;
		}
	}
	private _onMouseMove = (e: MouseEvent) => {
		if (!this._transferData.id) {
			return;
		}

		const {pageX, pageY} = e;
		if (!this._transferData.ghost) {
			if (Math.abs(this._transferData.x - pageX) < 3 && Math.abs(this._transferData.y - pageY) < 3) {
				return;
			} else {
				const ghost = this._onDragStart(this._transferData.id, this._transferData.targetId);
				if (!ghost) {
					this._endDrop();
					return;
				} else {
					this._transferData.ghost = ghost;
					document.body.appendChild(this._transferData.ghost);
				}
			}
		}
		this._moveGhost(pageX, pageY);
		this._onDrag(e);
	}
	private _onMouseUp = () => {
		if (!this._transferData.x) {
			return;
		}
		if (this._transferData.ghost) {
			this._removeGhost();
			this._onDrop();
		} else {
			this._endDrop();
		}

		document.removeEventListener("mousemove", this._onMouseMove);
		document.removeEventListener("mouseup", this._onMouseUp);

	}
	private _moveGhost(x: number, y: number) {
		if (this._transferData.ghost) {
			this._transferData.ghost.style.left = x - this._transferData.initXOffset + "px";
			this._transferData.ghost.style.top = y - this._transferData.initYOffset +  "px";
		}
	}
	private _removeGhost() {
		document.body.removeChild(this._transferData.ghost);
	}
	private _onDrop() {
		if (!this._canMove) {
			this._endDrop();
			return;
		}

		const target = collectionStore.getItem(this._lastCollectionId);
		const config = target && target.config;
		if (!target || config.dragMode === DragMode.source) {
			this._endDrop();
			return;
		}
		if (target.events.fire(DragEvents.beforeDrop, [this._lastId, this._transferData.target])) {
			const to = {
				id: this._lastId,
				target
			};
			const from = {
				id: this._transferData.id,
				target: this._transferData.target
			};
			this._move(from, to);
			to.target.events.fire(DragEvents.dropComplete, [to.id, this._transferData.dropPosition]);
		}
		this._endDrop();
	}
	private _onDragStart(id: string, targetId: string) {
		const target = collectionStore.getItem(targetId);
		const config = target.config;
		if (config.dragMode === DragMode.target) {
			return null;
		}
		const item = target.data.getItem(id);
		const ghost = dragEventContent(this._transferData.item, this._itemsForGhost);
		const ans = target.events.fire(DragEvents.beforeDrag, [item, ghost]);
		if (!ans || !id) {
			return null;
		}
		target.events.fire(DragEvents.dragStart, [id, this._selectedIds]);
		this._toggleTextSelection(true);
		this._transferData.target = target;
		this._transferData.dragConfig = config;
		return ghost;
	}
	private _onDrag(e: MouseEvent) {
		const {clientX, clientY} = e;
		const element = document.elementFromPoint(clientX, clientY);

		const collectionId = locate(element, "dhx_widget_id");
		if (!collectionId) {
			if (this._canMove) {
				this._cancelCanDrop();
			}
			return;
		}

		const target = collectionStore.getItem(collectionId);
		const id = locate(element, "dhx_id");

		if (!id) {
			this._cancelCanDrop();
			this._lastCollectionId = collectionId;
			this._lastId = null;
			this._canDrop();
			return;
		}


		if (target.config.dropBehaviour === DropBehaviour.complex) {
			const pos = getPosition(e);
			if (pos <= 0.25) {
				this._transferData.dropPosition = DropPosition.top;
			} else if (pos >= 0.75) {
				this._transferData.dropPosition = DropPosition.bot;
			} else {
				this._transferData.dropPosition = DropPosition.in;
			}
		} else if (this._lastId === id && this._lastCollectionId === collectionId) {
			return;
		}

		const from: ICopyObject = {
			id: this._transferData.id,
			target: this._transferData.target
		};
		if (target.config.dragMode === "source") {
			return;
		}
		from.target.events.fire(DragEvents.dragOut, [id, target]);

		if (collectionId !== this._transferData.targetId || !isTreeCollection(from.target.data) ||
			(isTreeCollection(from.target.data) && from.target.data.canCopy(from.id, id))) {
			this._cancelCanDrop(); // clear last
			this._lastId = id;
			this._lastCollectionId = collectionId;
			const canMove = from.target.events.fire(DragEvents.dragIn, [id, this._transferData.dropPosition, collectionStore.getItem(collectionId)]);
			if (canMove) {
				this._canDrop();
			}
		} else {
			this._cancelCanDrop();
		}
	}
	private _move(from: ICopyObject, to: ICopyObject): void {
		const fromData = from.target.data;
		const toData = to.target.data;
		let index = 0;
		let targetId = to.id;
		const behaviour = isTreeCollection(toData) ? to.target.config.dropBehaviour : undefined;

		switch(behaviour) {
			case DropBehaviour.child:
				break;
			case DropBehaviour.sibling:
				targetId = (toData as TreeCollection).getParent(targetId);
				index = toData.getIndex(to.id) + 1;
				break;
			case DropBehaviour.complex:
				const dropPosition = this._transferData.dropPosition;
				if (dropPosition === DropPosition.top) {
					targetId = (toData as TreeCollection).getParent(targetId);
					index = toData.getIndex(to.id);
				} else if (dropPosition === DropPosition.bot) {
					targetId = (toData as TreeCollection).getParent(targetId);
					index = toData.getIndex(to.id) + 1;
				}
				break;
			default:
				// list move
				if (!to.id) {
					index = -1;
				} else if (from.target === to.target && toData.getIndex(from.id) < toData.getIndex(to.id)) {
					index = toData.getIndex(to.id) - 1;
				} else {
					index = toData.getIndex(to.id);
				}
		}

		if (this._transferData.dragConfig.dragCopy) {
			if (this._selectedIds instanceof Array && this._selectedIds.length > 1) {
				this._selectedIds.map(selctedId => {
					fromData.copy(selctedId, index, toData, targetId);
					if (index > -1) {
						index++;
					}
				});
			} else {
				fromData.copy(from.id, index, toData, targetId);
			}
		} else {
			if (this._selectedIds instanceof Array && this._selectedIds.length > 1) {
				this._selectedIds.map(selctedId => {
					(fromData as any).move(selctedId, index, toData, targetId);
					if (index > -1) {
						index++;
					}
				});
			} else {
				(fromData as any).move(from.id, index, toData, targetId); // typescript bug??
			}
		}
	}
	private _endDrop() {
		this._toggleTextSelection(false);
		if (this._transferData.target) {
			this._transferData.target.events.fire(DragEvents.dragEnd, [this._transferData.id, this._selectedIds]);
		}
		this._cancelCanDrop();
		this._canMove = true;
		this._transferData = {};
		this._lastId = null;
		this._lastCollectionId = null;
	}
	private _cancelCanDrop() {
		this._canMove = false;
		const collection = collectionStore.getItem(this._lastCollectionId);
		if (collection && this._lastId) {
			collection.events.fire(DragEvents.cancelDrop, [this._lastId]);
		}
		this._lastCollectionId = null;
		this._lastId = null;
	}
	private _canDrop() {
		this._canMove = true;

		const target = collectionStore.getItem(this._lastCollectionId);
		if (target && this._lastId) {
			target.events.fire(DragEvents.canDrop, [this._lastId, this._transferData.dropPosition]);
		}
	}
	private _toggleTextSelection(add: boolean) {
		if (add) {
			document.body.classList.add("dhx_no-select");
		} else {
			document.body.classList.remove("dhx_no-select");
		}
	}
}

const dhx = (window as any).dhxHelpers = (window as any).dhxHelpers || {};
dhx.dragManager = dhx.dragManager || new DragManager();
export const dragManager = dhx.dragManager;